<template>
  <div class="keyboard-container">
    <div class="keyboard-grid">
      <div v-for="letter in SWEDISH_ALPHABET_LOWERCASE" :key="letter" class="letter-container word-grid">
        <v-btn
          @click="$emit('letterClick', letter)"
          :disabled="disabled || guessedLetters.has(letter)"
          :class="getLetterStyles(letter)"
          class="letter-button"
          variant="text"
          size="xs"
        >
          {{ letter }}

          <!-- Strike-through for incorrect letters -->
          <div v-if="getLetterStatus(letter) === 'incorrect'" class="strike-through" />
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SWEDISH_ALPHABET_LOWERCASE } from '../data/swedishAlphabet';
import { onMounted } from 'vue';

interface Props {
  guessedLetters: Set<string>;
  currentWord: string;
  disabled: boolean;
}

const props = defineProps<Props>();

const emits = defineEmits<{
  letterClick: [letter: string];
}>();

const getLetterStatus = (letter: string) => {
  if (!props.guessedLetters.has(letter)) return 'unused';
  return props.currentWord.includes(letter) ? 'correct' : 'incorrect';
};

const getLetterStyles = (letter: string) => {
  const status = getLetterStatus(letter);

  switch (status) {
    case 'correct':
      return 'letter-correct';
    case 'incorrect':
      return 'letter-incorrect';
    default:
      return 'letter-unused';
  }
};

const handleKeyPress = (event: any) => {
  if (
    SWEDISH_ALPHABET_LOWERCASE.includes(event.key.toLowerCase()) &&
    !props.guessedLetters.has(event.key.toLowerCase())
  ) {
    emits('letterClick', event.key.toLowerCase());
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeyPress);
});
</script>

<style scoped>
.word-container {
  width: 100%;
  margin: 0 auto;
}

.word-grid {
  display: grid;
  gap: 16px;
  justify-items: center;
}

.keyboard-container {
  width: 100%;
  max-width: calc(min(80%, 1200px));
  max-height: 162px;
  margin: 0 auto;
}

.keyboard-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.letter-button {
  font-family: 'Chalkduster', sans-serif;
  font-size: 2rem !important;
  font-weight: bold !important;
  user-select: none;
  min-width: unset !important;
  padding: 8px !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;

  @media screen and (min-width: 1440px) {
    font-size: 4rem !important;
  }
}

.letter-unused {
  color: white !important;
  opacity: 0.9;
}

.letter-unused:hover {
  opacity: 1 !important;
}

.letter-correct {
  color: white !important;
  opacity: 0;
  cursor: not-allowed !important;
}

.letter-incorrect {
  color: white !important;
  opacity: 0.3;
  cursor: not-allowed !important;
}

.strike-through {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 4px;
  opacity: 1 !important;
  background: linear-gradient(90deg, transparent 0%, white 30%, white 80%, transparent 100%) !important;
  transform: translateY(-50%) rotate(12deg);
  filter: blur(0.3px);
  pointer-events: none;
}
</style>
